import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { authApi } from '../lib/api';

interface AuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  /** Login diretto con le credenziali della casella Merak/IceWarp (nessuna registrazione). */
  loginWithMailCredentials: (email: string, password: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  init: () => Promise<void>;
}

/** Traduce i codici errore dell'API in messaggi utente. */
function loginErrorMessage(code: string): string {
  switch (code) {
    case 'invalid_mail_credentials':
      return 'Credenziali della casella di posta non valide';
    case 'domain_not_allowed':
      return 'Dominio non abilitato su questo portale';
    case 'merak_not_configured':
      return 'Portale non configurato: contatta il supporto Netlab';
    case 'invalid_input':
      return 'Email o password non validi';
    default:
      return 'Errore di accesso, riprova tra qualche istante';
  }
}

export const useAuth = create<AuthState>((set) => ({
  session: null,
  user: null,
  loading: true,

  init: async () => {
    const { data } = await supabase.auth.getSession();
    set({ session: data.session, user: data.session?.user ?? null, loading: false });
    supabase.auth.onAuthStateChange((_evt, session) => {
      set({ session, user: session?.user ?? null });
    });
  },

  signIn: async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    return {};
  },

  loginWithMailCredentials: async (email, password) => {
    try {
      const { session } = await authApi.loginWithMailCredentials(email, password);
      const { error } = await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      });
      if (error) return { error: error.message };
      return {};
    } catch (err) {
      return { error: loginErrorMessage((err as Error).message) };
    }
  },

  signOut: async () => {
    await supabase.auth.signOut();
  },
}));
